import type { ReactElement } from "react";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../../../../shared/components/ui/table";
import type { BulkEditTitle } from "../types";
import { VideoAssetsRow } from "./VideoAssetsRow";

interface VideoAssetsTableProps {
  readonly titles: BulkEditTitle[];
  readonly selectedIds: Set<string>;
  readonly onSelect: (titleId: string, selected: boolean) => void;
  readonly onSelectAll: (selected: boolean) => void;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
  readonly onRefresh?: (titleId: string) => Promise<void>;
}

/**
 * Table view for Video Assets editing
 * Shows: Feature video status + subtitles, Trailer video status + subtitles
 */
export function VideoAssetsTable({
  titles,
  selectedIds,
  onSelect,
  onSelectAll,
  onDelete,
  onDuplicate,
  onRefresh,
}: Readonly<VideoAssetsTableProps>): ReactElement {
  const allSelected = titles.length > 0 && titles.every((t) => selectedIds.has(t.id));

  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-neutral-900/50">
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => onSelectAll(checked === true)}
                aria-label="Select all"
              />
            </TableHead>
            <TableHead className="w-[250px] flex pt-[14px]">Title</TableHead>
            <TableHead className="w-[240px]">Feature Video</TableHead>
            <TableHead className="w-[190px]">Feature Subs</TableHead>
            <TableHead className="w-[240px]">Trailer Video</TableHead>
            <TableHead className="w-[190px]">Trailer Subs</TableHead>
            <TableHead className="w-[180px]">Status</TableHead>
            <TableHead className="w-[100px] text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {titles.map((title) => (
            <VideoAssetsRow
              key={title.id}
              title={title}
              isSelected={selectedIds.has(title.id)}
              onSelect={onSelect}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onRefresh={onRefresh}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
